import { portalColors, portalShadow, portalRadius } from './portalStyles';
import { ChecklistSection } from './ChecklistSection';
import { StickyNote, Navigation } from 'lucide-react';
import type { CrewPortalJob } from '../../types/crewPortal';

interface Props {
  job: CrewPortalJob;
  accentColor: string;
  dateLabel: string;
  isPast: boolean;
  isExpanded: boolean;
  onExpand: () => void;
  firstName: string;
}

function formatTime(time: string | null): string {
  if (!time) return '';
  const [h, m] = time.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12}:${m} ${ampm}`;
}

function getMapUrl(job: CrewPortalJob): string | null {
  const loc = job.location;
  if (!loc) return null;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (loc.latitude && loc.longitude) {
    return isIOS
      ? `maps://maps.apple.com/?daddr=${loc.latitude},${loc.longitude}`
      : `https://www.google.com/maps/dir/?api=1&destination=${loc.latitude},${loc.longitude}`;
  }
  const addr = [loc.address, loc.city, loc.province].filter(Boolean).join(', ');
  if (!addr) return null;
  return isIOS
    ? `maps://maps.apple.com/?daddr=${encodeURIComponent(addr)}`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`;
}

export function JobCompactCard({ job, accentColor, dateLabel, isPast, isExpanded, onExpand, firstName }: Props) {
  const locationName = job.location?.name ?? null;
  const itemCount = job.checklist?.length ?? 0;
  const meta = [locationName, itemCount > 0 ? `${itemCount} items` : null].filter(Boolean).join(' \u00B7 ');
  const statusLabel = isPast && job.status === 'completed' ? ' \u00B7 Done' : '';
  const timeStr = formatTime(job.scheduled_time);
  const hasChecklist = job.checklist && job.checklist.length > 0;
  const mapUrl = getMapUrl(job);

  return (
    <div
      onClick={onExpand}
      style={{
        background: portalColors.card, borderRadius: portalRadius.button,
        boxShadow: portalShadow.subtle, padding: 16, cursor: 'pointer',
        borderLeft: isExpanded ? `4px solid ${accentColor}` : '4px solid transparent',
        opacity: isPast && !isExpanded ? 0.5 : 1,
        transition: 'border-left 200ms, opacity 200ms',
        overflow: 'hidden',
      }}
    >
      {/* Date + time */}
      <div style={{ fontSize: 12, fontWeight: 500, color: accentColor }}>
        {dateLabel}{timeStr ? ` \u00B7 ${timeStr}` : ''}{statusLabel}
      </div>

      {/* Title */}
      <div style={{ fontSize: 16, fontWeight: 500, color: portalColors.primary, marginTop: 4 }}>
        {job.title}
      </div>

      {/* Meta */}
      {meta && (
        <div style={{ fontSize: 13, color: portalColors.secondary, marginTop: 2 }}>{meta}</div>
      )}

      {/* Expanded content */}
      {isExpanded && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: `0.5px solid ${portalColors.subtleDivider}` }}>
          {/* Notes */}
          {job.notes && (
            <div style={{
              display: 'flex', gap: 8, alignItems: 'flex-start',
              background: `${accentColor}0F`, borderRadius: 10,
              padding: '12px 14px', marginBottom: 12,
            }}>
              <StickyNote size={16} color={accentColor} style={{ flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: 13, color: portalColors.secondary, lineHeight: '18px' }}>{job.notes}</span>
            </div>
          )}

          {/* Navigate (only for future jobs) */}
          {!isPast && mapUrl && (
            <a
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                width: '100%', height: 44, borderRadius: portalRadius.button,
                background: accentColor, color: '#fff', textDecoration: 'none',
                fontSize: 14, fontWeight: 600, marginBottom: hasChecklist ? 4 : 0,
              }}
            >
              <Navigation size={16} />
              Navigate
            </a>
          )}

          {/* Checklist */}
          {hasChecklist && (
            <ChecklistSection
              checklist={job.checklist!}
              jobId={job.id}
              accentColor={accentColor}
              firstName={firstName}
            />
          )}
        </div>
      )}
    </div>
  );
}
